const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 150;
const MAX_RESULTS = 8;

const normalise = (str) =>
  str
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapeHtml = (str) =>
  str.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);

// "Non-rural offer" -> "non-rural-offer", matching the data-letter-type on each template
const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const escapeRegExp =(str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Set per environment in .env.development / .env.production. When empty (dev),
// the bundled sample data is used instead.
const LOOKUP_URL = process.env.OLP_LOOKUP_URL;

let addressesPromise = null;
let addressesLoaded = false;

function fetchRows() {
  if (LOOKUP_URL) {
    return fetch(LOOKUP_URL).then((res) => {
      if (!res.ok) throw new Error(`Address lookup request failed: ${res.status}`);
      return res.json();
    });
  }

  // Sample data is for local development only; this branch is stripped from production builds.
  if (process.env.NODE_ENV !== "production") {
    return import("../assets/data/sample/olp-lookup.sample.json").then(
      (module) => module.default || module,
    );
  }

  return Promise.reject(new Error("OLP_LOOKUP_URL is not set"));
}

// Lazily load the data and merge duplicate addresses, keeping every distinct letter type.
function loadAddresses() {
  if (!addressesPromise) {
    addressesPromise = fetchRows()
      .then((rows) => {
        const byAddress = new Map();

        rows.forEach(({ address, letter_type: letterType }) => {
          if (!address) return;
          if (!byAddress.has(address)) {
            byAddress.set(address, {
              address,
              search: normalise(address),
              letterTypes: [],
            });
          }
          const entry = byAddress.get(address);
          if (letterType && !entry.letterTypes.includes(letterType)) {
            entry.letterTypes.push(letterType);
          }
        });

        addressesLoaded = true;
        return [...byAddress.values()];
      })
      .catch((err) => {
        // Allow a retry on the next interaction.
        addressesPromise = null;
        throw err;
      });
  }
  return addressesPromise;
}

// Every typed token must match the start of a word in the address.
function findMatches(addresses, query) {
  const normalisedQuery = normalise(query);
  const tokens = normalisedQuery.split(" ");
  const tokenPatterns = tokens.map(
    (token) => new RegExp(`(^| )${escapeRegExp(token)}`),
  );

  const matches = addresses.filter((entry) =>
    tokenPatterns.every((pattern) => pattern.test(entry.search)),
  );

  // Addresses that start with the query come first.
  matches.sort((a, b) => {
    const aStarts = a.search.startsWith(normalisedQuery) ? 0 : 1;
    const bStarts = b.search.startsWith(normalisedQuery) ? 0 : 1;
    return aStarts - bStarts || a.address.localeCompare(b.address);
  });

  return { matches: matches.slice(0, MAX_RESULTS), tokens };
}

function highlight(address, tokens) {
  const pattern = new RegExp(
    `(^|[^A-Za-z0-9])(${tokens.map(escapeRegExp).join("|")})`,
    "gi",
  );
  return escapeHtml(address).replace(
    pattern,
    (_, boundary, match) => `${boundary}<strong>${match}</strong>`,
  );
}

export function olpLookup(root = document) {
  root.querySelectorAll(".olp-lookup").forEach((component) => {
    if (component.__olpLookupInitialized) return;
    component.__olpLookupInitialized = true;

    const input = component.querySelector(".olp-lookup-input");
    const form = component.querySelector(".olp-lookup-form");
    const resultBlock = component.querySelector(".olp-lookup-result");
    if (!input || !form || !resultBlock) return;

    const resultsContainer = document.createElement("div");
    resultsContainer.className = "olp-lookup-results hidden";
    resultsContainer.id = "olp-lookup-results";
    resultsContainer.setAttribute("role", "listbox");
    resultsContainer.setAttribute("aria-live", "polite");
    form.parentNode.appendChild(resultsContainer);

    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-controls", resultsContainer.id);
    input.setAttribute("aria-expanded", "false");

    let debounceTimer = null;
    let currentFocus = -1;
    let latestQuery = "";

    const setOpen = (open) => {
      resultsContainer.classList.toggle("hidden", !open);
      input.setAttribute("aria-expanded", String(open));
      if (!open) currentFocus = -1;
    };

    const clearResult = () => {
      resultBlock.hidden = true;
      resultBlock.innerHTML = "";
    };

    // The editable result text (rich text from the CMS) lives in one <template> per
    // letter type, keyed by data-letter-type.
    const buildMessage = (letterType) => {
      const resultTemplate = component.querySelector(
        `.olp-lookup-template[data-letter-type="${slugify(letterType)}"]`,
      );
      const html = resultTemplate ? resultTemplate.innerHTML.trim() : "";
      return html ? `<div class="olp-lookup-result-text">${html}</div>` : "";
    };

    const showResult = (entry) => {
      // One text block per letter type; types with no text are skipped.
      const messages = entry.letterTypes.map(buildMessage).join("");

      resultBlock.innerHTML = `
        <p class="olp-lookup-result-address">${escapeHtml(entry.address)}</p>
        ${messages}
      `;
      resultBlock.hidden = false;
    };

    const selectEntry = (entry) => {
      input.value = entry.address;
      setOpen(false);
      showResult(entry);
    };

    const showMessage = (message) => {
      currentFocus = -1;
      resultsContainer.innerHTML = `<div class="olp-lookup-no-results">${message}</div>`;
      setOpen(true);
    };

    const showNoResults = () => showMessage("No matching addresses found");

    const renderResults = (matches, tokens) => {
      if (!matches.length) {
        showNoResults();
        return;
      }

      currentFocus = -1;
      resultsContainer.innerHTML = matches
        .map(
          (entry, index) => `<a
            href="#"
            role="option"
            class="olp-lookup-result-item${index === matches.length - 1 ? " last" : ""}"
            data-index="${index}"
          >${highlight(entry.address, tokens)}</a>`,
        )
        .join("");

      resultsContainer
        .querySelectorAll(".olp-lookup-result-item")
        .forEach((item) => {
          item.addEventListener("click", (e) => {
            e.preventDefault();
            selectEntry(matches[Number(item.dataset.index)]);
          });
        });

      setOpen(true);
    };

    const search = async (query) => {
      latestQuery = query;
      // The dataset can take a few seconds to arrive on first use; say so rather than staying silent.
      if (!addressesLoaded) showMessage("Loading addresses…");
      let addresses;
      try {
        addresses = await loadAddresses();
      } catch (err) {
        if (query === latestQuery) showMessage("Unable to load addresses. Please try again.");
        return;
      }
      if (query !== latestQuery) return;

      const { matches, tokens } = findMatches(addresses, query);
      renderResults(matches, tokens);
    };

    const setActive = (items) => {
      items.forEach((item) => item.classList.remove("active"));
      if (items[currentFocus]) {
        items[currentFocus].classList.add("active");
        items[currentFocus].scrollIntoView({ block: "nearest" });
      }
    };

    const prefetch = () => loadAddresses().catch(() => {});

    // The endpoint is slow to respond, so start fetching as soon as the page loads.
    prefetch();

    // Retry on focus if the initial load failed (a no-op when it is already loading or loaded).
    input.addEventListener("focus", prefetch);

    input.addEventListener("input", (e) => {
      clearResult();
      clearTimeout(debounceTimer);

      const query = e.target.value.trim();
      if (normalise(query).length < MIN_QUERY_LENGTH) {
        latestQuery = "";
        setOpen(false);
        return;
      }

      debounceTimer = setTimeout(() => search(query), DEBOUNCE_MS);
    });

    input.addEventListener("keydown", (e) => {
      const items = resultsContainer.querySelectorAll(".olp-lookup-result-item");

      if (e.key === "ArrowDown" && items.length) {
        e.preventDefault();
        currentFocus = currentFocus >= items.length - 1 ? 0 : currentFocus + 1;
        setActive(items);
      } else if (e.key === "ArrowUp" && items.length) {
        e.preventDefault();
        currentFocus = currentFocus <= 0 ? items.length - 1 : currentFocus - 1;
        setActive(items);
      } else if (e.key === "Enter" && currentFocus > -1 && items[currentFocus]) {
        e.preventDefault();
        items[currentFocus].click();
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      clearTimeout(debounceTimer);

      const query = input.value.trim();
      if (!query) return;

      let addresses;
      try {
        addresses = await loadAddresses();
      } catch (err) {
        showMessage("Unable to load addresses. Please try again.");
        return;
      }
      const exact = addresses.find(
        (entry) => entry.search === normalise(query),
      );

      if (exact) {
        selectEntry(exact);
      } else {
        search(query);
      }
    });

    document.addEventListener("click", (e) => {
      if (!form.parentNode.contains(e.target)) setOpen(false);
    });
  });
}
